import {CustomPropertyMultiSelectPanel, CustomPropertySingleSelectPanel} from '@github-ui/custom-properties-editing'
import type {PropertyValue, ValueType} from '@github-ui/custom-properties-types'
import {Checkbox, FormControl, TextInput} from '@primer/react'
import {useEffect, useRef} from 'react'

import styles from './RequiredDefinitionEditor.module.css'

interface Props {
  checkboxLabel?: string
  checkboxCaption?: string
  defaultValueCaption?: string
  isRequired: boolean
  valueType: ValueType
  defaultValue: PropertyValue
  defaultValueError?: string
  allowableDefaultValues: string[]
  propertyName: string
  onRequiredChange: (required: boolean) => void
  onDefaultValueChange: (value: PropertyValue) => void
  setDefaultValueInputRef?: (ref: HTMLElement | null) => void
  className?: string
}

export function RequiredDefinitionEditor({
  checkboxLabel = 'Require this property for all targets',
  checkboxCaption = "Targets that don't have an explicit value for this property will inherit the default value.",
  defaultValueCaption = 'This is the default value that will be set for this property.',
  isRequired,
  valueType,
  defaultValue,
  defaultValueError,
  allowableDefaultValues: providedDefaultValues,
  propertyName,
  onRequiredChange,
  onDefaultValueChange,
  setDefaultValueInputRef,
  className,
}: Props) {
  const localStringDefaultValueRef = useRef<HTMLInputElement>(null)
  const localSingleSelectDefaultValueRef = useRef<HTMLElement>(null)
  const localBooleanDefaultValueRef = useRef<HTMLElement>(null)
  const localMultiSelectDefaultValueRef = useRef<HTMLElement>(null)

  const isText = valueType === 'string'
  const isSingleSelect = valueType === 'single_select'
  const isTrueFalse = valueType === 'true_false'
  const isMultiSelect = valueType === 'multi_select'
  const isUrl = valueType === 'url'

  useEffect(() => {
    if (setDefaultValueInputRef) {
      switch (valueType) {
        case 'string':
        case 'url':
          setDefaultValueInputRef(localStringDefaultValueRef.current)
          break
        case 'single_select':
          setDefaultValueInputRef(localSingleSelectDefaultValueRef.current)
          break
        case 'true_false':
          setDefaultValueInputRef(localBooleanDefaultValueRef.current)
          break
        case 'multi_select':
          setDefaultValueInputRef(localMultiSelectDefaultValueRef.current)
          break
      }
    }
  }, [setDefaultValueInputRef, valueType])

  const defaultValueInvalid = !!defaultValueError
  const defaultValueAriaAriaProp = {
    'aria-invalid': defaultValueInvalid,
    'aria-labelledby': 'property-default-value-label',
    'aria-describedby': `${
      defaultValueInvalid ? 'property-default-value-validation' : ''
    } property-default-value-caption`,
  }

  const allowableDefaultValues = (isTrueFalse ? ['true', 'false'] : providedDefaultValues) || null

  return (
    <div className={className}>
      <FormControl>
        <Checkbox checked={isRequired} onChange={() => onRequiredChange(!isRequired)} />
        <FormControl.Label>{checkboxLabel}</FormControl.Label>
        <FormControl.Caption>{checkboxCaption}</FormControl.Caption>
      </FormControl>
      <div className={styles.optionsInputGroup}>
        {isRequired && (
          <FormControl className={styles.defaultValueFormControl}>
            <FormControl.Label id="property-default-value-label">Default value *</FormControl.Label>
            {isMultiSelect && (
              <div className={styles.selectPanelContainer}>
                <CustomPropertyMultiSelectPanel
                  anchorRef={localMultiSelectDefaultValueRef}
                  propertyValue={defaultValue as string[]}
                  mixed={false}
                  onChange={values => onDefaultValueChange(values)}
                  propertyName={propertyName}
                  allowedValues={allowableDefaultValues}
                  defaultValue={null}
                  anchorProps={defaultValueAriaAriaProp}
                />
              </div>
            )}

            {isSingleSelect && (
              <div className={styles.selectPanelContainer}>
                <CustomPropertySingleSelectPanel
                  anchorRef={localSingleSelectDefaultValueRef}
                  propertyValue={defaultValue as string}
                  mixed={false}
                  onChange={values => onDefaultValueChange(values)}
                  propertyName={propertyName}
                  allowedValues={allowableDefaultValues}
                  defaultValue={null}
                  anchorProps={defaultValueAriaAriaProp}
                />
              </div>
            )}

            {isTrueFalse && (
              <div className={styles.selectPanelContainer}>
                <CustomPropertySingleSelectPanel
                  anchorRef={localBooleanDefaultValueRef}
                  propertyValue={defaultValue as string}
                  mixed={false}
                  onChange={onDefaultValueChange}
                  propertyName={propertyName}
                  allowedValues={['true', 'false']}
                  defaultValue={null}
                  anchorProps={defaultValueAriaAriaProp}
                />
              </div>
            )}

            {(isText || isUrl) && (
              <TextInput
                block
                value={defaultValue}
                ref={localStringDefaultValueRef}
                onChange={e => onDefaultValueChange(e.target.value)}
                {...defaultValueAriaAriaProp}
              />
            )}

            {defaultValueError && (
              <FormControl.Validation
                id="property-default-value-validation"
                variant="error"
                className={styles.validationFeedback}
              >
                {defaultValueError}
              </FormControl.Validation>
            )}
            <FormControl.Caption id="property-default-value-caption">{defaultValueCaption}</FormControl.Caption>
          </FormControl>
        )}
      </div>
    </div>
  )
}

try{ RequiredDefinitionEditor.displayName ||= 'RequiredDefinitionEditor' } catch {}